import { useMultipleFeatureFlags } from '@agentnet/feature-flags';
import { AsyncStatus } from 'features/files/fincen-reporting/constants';
import { useMarketPlace } from 'hooks/useMarketPlace';
import { useCallback, useEffect, useMemo } from 'react';
import { FeatureFlag } from 'utilities/constants/launch-darkly-flags';
import { buildFirmUnderwriterOptions, UnderwriterOption } from './underwriterOptions';

export interface FirmUnderwriterOptions {
  // Null until the firm's underwriters are available, so callers can keep their existing options.
  options: UnderwriterOption[] | null;
  isLoading: boolean;
  isNonFaUnderwriter: (underwriterCode?: string) => boolean;
}

export function useFirmUnderwriterOptions(isDrawerOpen: boolean): FirmUnderwriterOptions {
  const [nonFaUw] = useMultipleFeatureFlags(FeatureFlag.NON_FA_UW_ANS_679_ENABLED);
  const { fetchFirmUnderwriters, fetchFirmUnderwritersResults, fetchFirmUnderwritersStatus } = useMarketPlace();

  const isNonFaUwEnabled = nonFaUw.ready && !!nonFaUw.value;
  const shouldFetch = isNonFaUwEnabled && isDrawerOpen;

  // fetchFirmUnderwriters comes from useAsync, and the underlying firmUnderwriters
  // function in useMarketPlace is recreated each render, so including it in deps
  // would cause repeated fetches hence excluding
  useEffect(() => {
    if (shouldFetch) {
      fetchFirmUnderwriters();
    }
  }, [shouldFetch]);

  const options = useMemo(() => {
    if (!isNonFaUwEnabled || !fetchFirmUnderwritersResults) {
      return null;
    }

    return buildFirmUnderwriterOptions(fetchFirmUnderwritersResults.underwriters);
  }, [isNonFaUwEnabled, fetchFirmUnderwritersResults]);

  // The dropdown options only carry name and code, so the raw list is the only place
  // that says whether an underwriter is external to First American.
  const isNonFaUnderwriter = useCallback(
    (underwriterCode?: string) => {
      if (!isNonFaUwEnabled || !underwriterCode) {
        return false;
      }

      return !!fetchFirmUnderwritersResults?.underwriters?.some(
        (underwriter) => underwriter.Code === underwriterCode && underwriter.IsExternalUnderwriter,
      );
    },
    [isNonFaUwEnabled, fetchFirmUnderwritersResults],
  );

  // Stay in the loading state until the fetch settles, otherwise the dropdown is briefly
  // interactive with the fallback options and a selection made then is lost when the firm list arrives.
  const isLoading = shouldFetch && !options && fetchFirmUnderwritersStatus !== AsyncStatus.ERROR;

  return {
    options,
    isLoading,
    isNonFaUnderwriter,
  };
}
